import { JSX, useMemo } from "react";

import { TierBanded, TierTobacco, VolLifeRate } from "../../constants/aquafina";
import {
  CoverageArrowRow,
  CoverageHeaderRow,
  CoverageRow,
} from "../table/rows";
import {
  CoverageCell,
  CoverageMultiVectorCell,
  CoverageMultiVectorHeaderCell,
  CoverageVectorCell,
  CoverageVectorHeaderCell,
} from "../table/cells";
import { RangeIterator } from "../../utils";
import { RATE_TYPES } from "../../constants/rateTypes";

type SingleValue = [string];
type TobaccoValues = [string, string]; // [nonTobacco, tobacco]
type RateValue = SingleValue | TobaccoValues;
type RateRowValue = RateValue[];

type ProcessedResult = {
  header: string[][];
  subheader: (SingleValue | TobaccoValues)[][];
  tiers: [string, RateRowValue[]][];
};

type RateMetadata = {
  hasSpouse: boolean;
  isEmployeeTobacco: boolean;
  isSpouseTobacco: boolean;
};

const EMPLOYEE_POSITION = 0;
const SPOUSE_POSITION = 1;

function createTierValues(metadata: RateMetadata): RateValue[] {
  const { hasSpouse, isEmployeeTobacco, isSpouseTobacco } = metadata;
  const rowValues: RateValue[] = [];

  rowValues[EMPLOYEE_POSITION] = isEmployeeTobacco ? ["", ""] : [""];

  if (hasSpouse) {
    rowValues[SPOUSE_POSITION] = isSpouseTobacco ? ["", ""] : [""];
  }

  return rowValues;
}

function setEmployeeTobaccoRate(
  tierValues: RateValue[],
  tier: TierTobacco,
): void {
  tierValues[EMPLOYEE_POSITION] = [tier.nonTobacco || "", tier.tobacco || ""];
}

function setEmployeeBandedRate(
  tierValues: RateValue[],
  tier: TierBanded,
): void {
  tierValues[EMPLOYEE_POSITION] = [tier.value || ""];
}

function setSpouseTobaccoRate(
  tierValues: RateValue[],
  tier: TierTobacco,
): void {
  tierValues[SPOUSE_POSITION] = [tier.nonTobacco || "", tier.tobacco || ""];
}

function setSpouseBandedRate(tierValues: RateValue[], tier: TierBanded): void {
  tierValues[SPOUSE_POSITION] = [tier.value || ""];
}

const VoluntaryLifeRates = ({
  rates,
}: {
  rates: VolLifeRate[];
}): JSX.Element => {
  const state: ProcessedResult = useMemo(() => {
    const rateMetadata: RateMetadata[] = [];
    const tierValuesMap = new Map<string, RateRowValue[]>();
    const _header: string[][] = [];
    const _subheader: RateValue[][] = [];

    function resolveTierValues(
      tierName: string,
      rateIndex: number,
      metadata: RateMetadata,
    ): RateValue[] {
      let tierEntries = tierValuesMap.get(tierName);
      if (!tierEntries) {
        tierEntries = (new Array(rates.length) as RateRowValue[]).fill(
          null as unknown as RateRowValue,
        );
        tierValuesMap.set(tierName, tierEntries);
      }

      if (!tierEntries[rateIndex]) {
        tierEntries[rateIndex] = createTierValues(metadata);
      }

      return tierEntries[rateIndex];
    }

    RangeIterator(rates.length, (rateIndex) => {
      const rate = rates[rateIndex];
      const hasSpouse = !!rate.spouse;
      const isEmployeeTobacco =
        rate.employee.type === RATE_TYPES.AGE_BANDED_TOBACCO;
      const isSpouseTobacco =
        hasSpouse && rate.spouse?.type === RATE_TYPES.AGE_BANDED_TOBACCO;

      const metadata = {
        hasSpouse,
        isEmployeeTobacco,
        isSpouseTobacco,
      };
      rateMetadata[rateIndex] = metadata;

      rate.employee.tiers.forEach((tier) => {
        const tierValues = resolveTierValues(tier.name, rateIndex, metadata);

        if (isEmployeeTobacco) {
          setEmployeeTobaccoRate(tierValues, tier as TierTobacco);
        } else {
          setEmployeeBandedRate(tierValues, tier as TierBanded);
        }
      });

      if (hasSpouse) {
        rate.spouse.tiers.forEach((tier) => {
          const tierValues = resolveTierValues(tier.name, rateIndex, metadata);

          if (isSpouseTobacco) {
            setSpouseTobaccoRate(tierValues, tier as TierTobacco);
          } else {
            setSpouseBandedRate(tierValues, tier as TierBanded);
          }
        });
      }

      _header.push(hasSpouse ? ["Employee", "Spouse"] : ["Employee"]);

      const columnLabels: RateValue[] = [];
      columnLabels.push(isEmployeeTobacco ? ["Non-Tobacco", "Tobacco"] : [""]);

      if (hasSpouse) {
        columnLabels.push(isSpouseTobacco ? ["Non-Tobacco", "Tobacco"] : [""]);
      }

      _subheader.push(columnLabels);
    });

    tierValuesMap.forEach((tierValues) => {
      RangeIterator(rates.length, (rateIndex) => {
        if (!tierValues[rateIndex]) {
          tierValues[rateIndex] = createTierValues(rateMetadata[rateIndex]);
        }
      });
    });

    const sortedTierNames = Array.from(tierValuesMap.keys()).sort((a, b) => {
      const getMinAge = (tier: string): number => {
        if (tier.startsWith("<")) {
          const match = tier.match(/< (\d+)/);
          return match ? parseInt(match[1], 10) - 1 : Number.MAX_SAFE_INTEGER;
        }

        const match = tier.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
      };

      return getMinAge(a) - getMinAge(b);
    });

    const tiersData: [string, RateRowValue[]][] = sortedTierNames.map(
      (tierName) => [tierName, tierValuesMap.get(tierName)!],
    );
    return {
      header: _header,
      subheader: _subheader,
      tiers: tiersData,
    };
  }, [rates]);

  const offset = state.tiers.length;

  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(rates.length, (index) => (
          <CoverageVectorHeaderCell label={state.header[index]} key={index} />
        ))}
      </CoverageHeaderRow>
      <CoverageRow label="Rate Unit">
        {RangeIterator(rates.length, (index) => (
          <CoverageVectorCell
            value={
              rates[index].spouse
                ? [
                    rates[index].employee?.rateUnit,
                    rates[index].spouse?.rateUnit,
                  ]
                : [rates[index].employee?.rateUnit]
            }
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Rates" isWhite>
        {RangeIterator(rates.length, (index) => (
          <CoverageMultiVectorHeaderCell
            labels={state.subheader[index]}
            key={index}
          />
        ))}
      </CoverageRow>
      {state.tiers.map(([label, values], indexTier) => (
        <CoverageArrowRow
          key={label}
          label={label}
          level={2}
          isWhite={indexTier % 2 !== 0}
        >
          {values.map((value, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <CoverageMultiVectorCell value={value} key={index} />
          ))}
        </CoverageArrowRow>
      ))}
      <CoverageRow label="AD&D Rate" isWhite={offset % 2 !== 0}>
        {RangeIterator(rates.length, (index) => (
          <CoverageVectorCell
            value={
              rates[index].spouse
                ? [rates[index].employeeAdndRate, rates[index].spouseAdndRate]
                : [rates[index].employeeAdndRate]
            }
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Child Life Rate" isWhite={offset % 2 === 0}>
        {RangeIterator(rates.length, (index) => (
          <CoverageCell value={rates[index].childLifeRate} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Child AD&D Rate" isWhite={offset % 2 !== 0}>
        {RangeIterator(rates.length, (index) => (
          <CoverageCell value={rates[index].childLifeAdndRate} key={index} />
        ))}
      </CoverageRow>
    </>
  );
};

export default VoluntaryLifeRates;
